import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, ElementRef, Inject, Input, Output, ViewChild, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { TextEditableComponent } from './text-editable.directive';
import { FikoYTIstekKok } from '@/src/service/modules/fikoyt-istek-kok/fikoyt.istek.makinesi.service';

interface IWindow extends Window {
  SpeechRecognition: any;
  webkitSpeechRecognition: any;
}

export interface IMentionKullanici {
  _id: string;
  isim: string;
  kullaniciAdi?: string;
}

@Component({
  selector: 'fikoyt-paylas-text-editor',
  templateUrl: './paylas-text-editor.component.html',
  styleUrls: ['./paylas-text-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    TextEditableComponent,
  ]
})
export class PaylasTextEditorComponent implements OnDestroy {
  @ViewChild('textEditor', { static: false }) textEditor: any;

  @Input() kaydetTusu = false;

  @Input() saltOkunur = false;

  // Ses tanıma özellikleri
  private recognition: any = null;
  sesKayitAktif = false;
  sesKayitDestekleniyor = false;
  geciciMetin = '';
  private sonMetin = '';
  // private YAZI: string;
  // public get yazi(): string {
  //   return this.YAZI;
  // }
  // @Input() public set yazi(v: string) {
  //   this.YAZI = v;
  // }

  private HTML: string | any;
  public get html(): string {
    return this.HTML;
  }
  @Input() public set html(v: string) {
    this.HTML = v;
    this.htmlChange.emit(v)
  }

  @Output() htmlChange: EventEmitter<string> = new EventEmitter<string>();

  @Output() tiklandiKaydet: EventEmitter<string> = new EventEmitter<string>();

  mentionAcik = false;
  mentionSonuclar: IMentionKullanici[] = [];
  mentionAramaMetni = '';
  mentionSeciliIndex = 0;
  private mentionAramaZamanlayici: any = null;
  private mentionSavedRange: Range | null = null;

  constructor(
    private elemref: ElementRef,
    @Inject(DOCUMENT) private document: Document,
    public sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef
  ) {
    this.sesKayitDestekleniyor = this.sesKayitBaslat();
  }

  ngOnDestroy(): void {
    if (this.recognition) {
      this.recognition.stop();
      this.recognition = null;
    }
    if (this.mentionAramaZamanlayici) clearTimeout(this.mentionAramaZamanlayici);
  }

  // Ses tanıma sistemini başlat
  private sesKayitBaslat(): boolean {
    const win = window as any as IWindow;
    const SpeechRecognition = win.SpeechRecognition || win.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      console.warn('Web Speech API desteklenmiyor');
      return false;
    }

    this.recognition = new SpeechRecognition();
    this.recognition.lang = 'tr-TR'; // Türkçe
    this.recognition.continuous = true; // Sürekli dinle
    this.recognition.interimResults = true; // Geçici sonuçları göster
    this.recognition.maxAlternatives = 1;

    // Ses tanıma başladığında
    this.recognition.onstart = () => {
      this.sesKayitAktif = true;
      this.geciciMetin = '';
      this.cdr.detectChanges();
    };

    // Ses tanıma sonucu geldiğinde
    this.recognition.onresult = (event: any) => {
      let gecici = '';
      let kesin = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;

        if (event.results[i].isFinal) {
          kesin += transcript + ' ';
        } else {
          gecici += transcript;
        }
      }

      // Kesin sonuç geldiyse text editor'a ekle
      if (kesin) {
        this.sonMetin += kesin;
        this.metniEkle(kesin);
        this.geciciMetin = gecici;
      } else {
        this.geciciMetin = gecici;
      }

      this.cdr.detectChanges();
    };

    // Ses tanıma durduğunda
    this.recognition.onend = () => {
      if (this.sesKayitAktif) {
        // Kullanıcı durdurdu ise tekrar başlatma
        try {
          this.recognition.start();
        } catch (e) {
          this.sesKayitAktif = false;
          this.cdr.detectChanges();
        }
      }
    };

    // Hata durumunda
    this.recognition.onerror = (event: any) => {
      console.error('Ses tanıma hatası:', event.error);
      if (event.error === 'no-speech') {
        // Ses yok hatası - normal, devam et
        return;
      }
      this.sesKayitAktif = false;
      this.cdr.detectChanges();
    };

    return true;
  }

  // Mikrofon butonuna basıldığında
  toggleSesKayit() {
    if (!this.sesKayitDestekleniyor) {
      alert('Tarayıcınız ses tanıma özelliğini desteklemiyor. Chrome veya Edge kullanın.');
      return;
    }

    if (this.sesKayitAktif) {
      // Durdur
      this.sesKayitAktif = false;
      this.recognition.stop();
      this.geciciMetin = '';
      this.sonMetin = '';
    } else {
      // Başlat
      try {
        this.recognition.start();
      } catch (e) {
        console.error('Ses tanıma başlatma hatası:', e);
      }
    }
    this.cdr.detectChanges();
  }

  // Metni text editor'a ekle
  private metniEkle(metin: string) {
    if (this.saltOkunur) return;

    // textEditor directive'inin DOM elementini bul
    const editorEl: HTMLElement = this.textEditor?.el?.nativeElement
      || this.elemref.nativeElement.querySelector('.aciklama-div');
    if (!editorEl) return;

    // Mevcut içeriğe metni sona ekle
    editorEl.focus();

    // execCommand ile sona ekle - ControlValueAccessor'ü tetikler
    const sel = this.document.getSelection();
    if (sel) {
      // Cursor'u sona taşı
      const range = this.document.createRange();
      range.selectNodeContents(editorEl);
      range.collapse(false); // false = sona git
      sel.removeAllRanges();
      sel.addRange(range);

      // insertText komutu - input event'i tetikler, ngModel güncellenir
      this.document.execCommand('insertText', false, metin);
    }
  }

  execCommand(command: string, showui = false, target: any = undefined) {
    this.document.execCommand(command, showui, target?.value);
  }

  editorFullScreen(elem: any) {
    if (!this.document.fullscreenElement) {
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen();
      }
    } else {
      if (this.document.exitFullscreen) {
        this.document.exitFullscreen();
        //@ts-ignore
      } else if (this.document.webkitExitFullscreen) {
        //@ts-ignore
        this.document.webkitExitFullscreen();
        //@ts-ignore
      } else if (this.document.msExitFullscreen) {
        //@ts-ignore
        this.document.msExitFullscreen();
      }
    }
  }

  editorKeydown(event: KeyboardEvent) {
    if (!this.mentionAcik) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.mentionSeciliIndex = Math.min(this.mentionSeciliIndex + 1, this.mentionSonuclar.length - 1);
      this.cdr.detectChanges();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.mentionSeciliIndex = Math.max(this.mentionSeciliIndex - 1, 0);
      this.cdr.detectChanges();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (this.mentionSonuclar[this.mentionSeciliIndex]) {
        this.mentionSec(this.mentionSonuclar[this.mentionSeciliIndex]);
      }
    } else if (event.key === 'Escape') {
      this.mentionKapat();
    }
  }

  editorInput() {
    if (this.saltOkunur) return;
    const sel = this.document.getSelection();
    if (!sel?.rangeCount) return;
    const range = sel.getRangeAt(0);
    const textNode = range.startContainer;
    if (textNode.nodeType !== Node.TEXT_NODE) {
      if (this.mentionAcik) this.mentionKapat();
      return;
    }
    const metin = textNode.textContent || '';
    const cursorPos = range.startOffset;
    const oncekiMetin = metin.substring(0, cursorPos);
    const atMatch = oncekiMetin.match(/@([^\s@]*)$/);
    if (atMatch) {
      this.mentionAramaMetni = atMatch[1];
      this.mentionSavedRange = range.cloneRange();
      this.mentionAcik = true;
      this.mentionSeciliIndex = 0;
      this.mentionAra(this.mentionAramaMetni);
    } else if (this.mentionAcik) {
      this.mentionKapat();
    }
  }

  private mentionAra(arama: string) {
    if (this.mentionAramaZamanlayici) clearTimeout(this.mentionAramaZamanlayici);
    this.mentionAramaZamanlayici = setTimeout(async () => {
      try {
        const match: any = { vt: 'FikoYT.sistem.kullanicilar', silindi: { $ne: true } };
        if (arama.trim()) {
          match.$or = [
            { 'veri.pilesmi': { $regex: arama, $options: 'i' } },
            { kullaniciAdi: { $regex: arama, $options: 'i' } },
          ];
        }
        const istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.hareketler.aggregate' });
        istek.parametreler = { aggregate: [
          { $match: match },
          { $project: { _id: 1, 'veri.pilesmi': 1, kullaniciAdi: 1 } },
          { $limit: 8 },
        ]};
        await istek.isteSync();
        const sonuclar = (istek.cevap?.sonuc || []) as any[];
        this.mentionSonuclar = sonuclar.map(s => ({
          _id: s._id,
          isim: s.veri?.pilesmi || s.kullaniciAdi || '?',
          kullaniciAdi: s.kullaniciAdi,
        }));
        this.mentionSeciliIndex = 0;
      } catch {
        this.mentionSonuclar = [];
      }
      this.cdr.detectChanges();
    }, 200);
  }

  mentionSec(kullanici: IMentionKullanici) {
    if (!this.mentionSavedRange) { this.mentionKapat(); return; }
    const sel = this.document.getSelection();
    if (!sel) { this.mentionKapat(); return; }
    sel.removeAllRanges();
    sel.addRange(this.mentionSavedRange);
    const range = sel.getRangeAt(0);
    const textNode = range.startContainer;
    if (textNode.nodeType !== Node.TEXT_NODE) { this.mentionKapat(); return; }
    const metin = textNode.textContent || '';
    const cursorPos = range.startOffset;
    const oncekiMetin = metin.substring(0, cursorPos);
    const atIdx = oncekiMetin.lastIndexOf('@');
    if (atIdx === -1) { this.mentionKapat(); return; }
    const mentionSpan = this.document.createElement('span');
    mentionSpan.className = 'mention';
    mentionSpan.setAttribute('data-user-id', kullanici._id);
    mentionSpan.setAttribute('contenteditable', 'false');
    mentionSpan.textContent = `@${kullanici.isim}`;
    const oncesi = metin.substring(0, atIdx);
    const sonrasi = metin.substring(cursorPos);
    textNode.textContent = oncesi;
    const sonrasiNode = this.document.createTextNode('\u00A0' + sonrasi);
    const parent = textNode.parentNode;
    if (parent) {
      parent.insertBefore(mentionSpan, textNode.nextSibling);
      parent.insertBefore(sonrasiNode, mentionSpan.nextSibling);
      const newRange = this.document.createRange();
      newRange.setStart(sonrasiNode, 1);
      newRange.collapse(true);
      sel.removeAllRanges();
      sel.addRange(newRange);
    }
    this.mentionKapat();
    if (this.textEditor?.callOnChange) this.textEditor.callOnChange();
  }

  mentionKapat() {
    this.mentionAcik = false;
    this.mentionSonuclar = [];
    this.mentionAramaMetni = '';
    this.mentionSavedRange = null;
    this.cdr.detectChanges();
  }

  static mentionlariParsele(html: string): string[] {
    const regex = /data-user-id="([^"]+)"/g;
    const idler: string[] = [];
    let match;
    while ((match = regex.exec(html)) !== null) idler.push(match[1]);
    return [...new Set(idler)];
  }
}
